import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuizStore } from "../state/store";
import { CounterHeader } from "./CounterHeader";
import { ProgressBar } from "./ProgressBar";
import { ResetDialog } from "./ResetDialog";
import { BrandMark } from "./BrandMark";

export function CompletionScreen() {
  const counters = useQuizStore((s) => s.counters);
  const progress = useQuizStore((s) => s.progress);
  const questionsAnswered = useQuizStore((s) => s.questionsAnswered);
  const learningMode = useQuizStore((s) => s.settings.learningMode);
  const markersSize = useQuizStore((s) => s.markers.size);
  const setLearningMode = useQuizStore((s) => s.setLearningMode);

  // Im Markiert-Modus mit leerem Pool (0 markers oder alle markierten gemeistert)
  // zeigt CompletionScreen eine spezifische Variante — die "Du hast alle Vokabeln
  // gemeistert!"-Glückwunsch-Meldung wäre dort falsch (Issue #123, Spec 50 §7).
  if (learningMode === "marked") {
    return (
      <MarkedEmptyScreen
        counters={counters}
        markersSize={markersSize}
        onSwitchToAll={() => void setLearningMode("all")}
      />
    );
  }

  const stats = useMemo(() => {
    let correct = 0;
    let wrong = 0;
    let firstAt = Number.POSITIVE_INFINITY;
    let lastAt = 0;
    for (const p of progress.values()) {
      correct += p.totalCorrect;
      wrong += p.totalWrong;
      if (p.lastSeenAt > 0 && p.lastSeenAt < firstAt) firstAt = p.lastSeenAt;
      if (p.lastSeenAt > lastAt) lastAt = p.lastSeenAt;
    }
    const accuracy = correct + wrong === 0 ? 0 : Math.round((correct / (correct + wrong)) * 100);
    return {
      correct,
      wrong,
      accuracy,
      firstAt: firstAt === Number.POSITIVE_INFINITY ? null : firstAt,
      lastAt: lastAt === 0 ? null : lastAt,
    };
  }, [progress]);

  const formatDate = (ts: number | null) =>
    ts == null
      ? "—"
      : new Date(ts).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });

  const elapsedMs = stats.firstAt && stats.lastAt ? stats.lastAt - stats.firstAt : 0;
  const elapsedLabel = formatElapsed(elapsedMs);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="completion-screen">
      <CounterHeader counters={counters} />
      <ProgressBar counters={counters} />

      <div className="relative bg-card rounded-2xl border border-success/30 shadow-card p-6 sm:p-8 space-y-5 text-center overflow-hidden">
        {/* Subtle celebratory gradient */}
        <div
          className="absolute inset-0 bg-gradient-to-br from-success/10 via-transparent to-accent/10 pointer-events-none"
          aria-hidden
        />

        <div className="relative space-y-3">
          <BrandMark
            variant="horizontal"
            className="h-16 sm:h-20 w-auto mx-auto"
            ariaLabel=""
          />
          <div className="text-5xl pt-2" aria-hidden>
            ✓
          </div>
          <h2 className="text-3xl font-bold text-success tracking-tight">Geschafft!</h2>
          <p className="text-fg-soft leading-relaxed max-w-md mx-auto">
            Du hast alle <span className="font-semibold text-fg">{counters.total}</span>{" "}
            Vokabeln gemeistert. Glückwunsch — du bist bereit für die PPL-Englisch-Prüfung.
          </p>
        </div>

        <dl className="relative grid grid-cols-2 sm:grid-cols-4 gap-3 text-left text-sm pt-2">
          <Stat label="Fragen" value={String(questionsAnswered)} />
          <Stat label="Richtig" value={String(stats.correct)} tone="success" />
          <Stat label="Falsch" value={String(stats.wrong)} tone="danger" />
          <Stat label="Trefferquote" value={`${stats.accuracy}%`} tone="accent" />
          <Stat label="Erste Antwort" value={formatDate(stats.firstAt)} />
          <Stat label="Letzte Antwort" value={formatDate(stats.lastAt)} />
          <Stat label="Spanne" value={elapsedLabel} />
        </dl>

        <div className="relative pt-2 flex flex-col sm:flex-row gap-3 justify-center items-center">
          <Link
            to="/zertifikat"
            data-testid="certificate-link"
            className="min-h-[52px] inline-flex items-center px-6 py-3 rounded-xl bg-success text-white text-base font-semibold shadow-card hover:bg-success/90 active:scale-[0.985] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-success focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
          >
            📜 Zertifikat ansehen + drucken
          </Link>
          <ResetDialog
            trigger={
              <button
                type="button"
                className="min-h-[52px] px-6 py-3 rounded-xl bg-accent text-white text-base font-semibold shadow-card hover:bg-accent-hover active:bg-accent-active active:scale-[0.985] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                data-testid="restart-button"
              >
                Von vorne anfangen
              </button>
            }
          />
        </div>
      </div>
    </div>
  );
}

const STAT_TONE: Record<string, string> = {
  default: "text-fg",
  success: "text-success",
  danger: "text-danger",
  accent: "text-accent",
};

function Stat({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: string;
  tone?: keyof typeof STAT_TONE;
}) {
  return (
    <div className="bg-bg/40 rounded-lg px-3 py-2.5 border border-outline-soft">
      <div className="text-[10px] uppercase tracking-wide text-fg-faint">{label}</div>
      <div className={`text-base font-semibold tabular ${STAT_TONE[tone]}`}>{value}</div>
    </div>
  );
}

function MarkedEmptyScreen({
  counters,
  markersSize,
  onSwitchToAll,
}: {
  counters: { total: number; mastered: number; inProgress: number; wrong: number };
  markersSize: number;
  onSwitchToAll: () => void;
}) {
  const hasNone = markersSize === 0;
  return (
    <div className="space-y-6 animate-fade-in" data-testid="marked-empty-screen">
      <CounterHeader counters={counters} />
      <ProgressBar counters={counters} />

      <div className="relative bg-card rounded-2xl border border-outline shadow-card p-6 sm:p-8 space-y-5 text-center overflow-hidden">
        <span aria-hidden className="absolute top-0 left-3 right-3 h-1 rounded-b-full bg-warning" />
        <div className="space-y-3">
          <div className="text-5xl pt-2" aria-hidden>
            ★
          </div>
          <h2 className="text-2xl font-bold text-fg tracking-tight">
            {hasNone ? "Noch keine Vokabeln markiert" : "Alle markierten Vokabeln gemeistert"}
          </h2>
          <p className="text-fg-soft leading-relaxed max-w-md mx-auto">
            {hasNone ? (
              <>
                Tippe im Quiz oder unter <span className="font-semibold text-fg">Meine Vokabeln</span> auf den
                Stern, um Wörter in deine Markiert-Liste aufzunehmen. Dann kannst du sie hier gezielt nachüben.
              </>
            ) : (
              <>
                Glückwunsch — alle Wörter in deiner Markiert-Liste sind im Hauptkurs auf{" "}
                <span className="font-semibold text-fg">gemeistert</span>. Wechsle zurück zum vollen Vokabel-
                Pool oder markiere weitere Wörter zum Nachüben.
              </>
            )}
          </p>
        </div>

        <div className="pt-2 flex flex-col sm:flex-row gap-3 justify-center items-center">
          <button
            type="button"
            onClick={onSwitchToAll}
            data-testid="switch-to-all"
            className="min-h-[52px] px-6 py-3 rounded-xl bg-accent text-accent-on text-base font-semibold shadow-card hover:bg-accent-hover active:bg-accent-active active:scale-[0.985] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
          >
            Zurück zum Hauptkurs
          </button>
        </div>
      </div>
    </div>
  );
}

function formatElapsed(ms: number): string {
  if (ms <= 0) return "—";
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  const minutes = Math.floor((ms % 3_600_000) / 60_000);
  if (days > 0) return `${days} T ${hours} h`;
  if (hours > 0) return `${hours} h ${minutes} min`;
  return `${minutes} min`;
}
